import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Smartphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Donate() {
  const [donationType, setDonationType] = useState('once'); // 'once' or 'monthly'
  const [selectedPackage, setSelectedPackage] = useState('medium');
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [donorFirstName, setDonorFirstName] = useState('');
  const [donorLastName, setDonorLastName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');

  // Package definitions matching backend
  const packages = {
    once: {
      small: { amount: 50, label: '$50', description: '1 child for 1 year' },
      medium: { amount: 100, label: '$100', description: '2 children for 1 year' },
      large: { amount: 150, label: '$150', description: '3 children for 1 year' },
      xlarge: { amount: 200, label: '$200', description: '4 children for 1 year' },
      classroom: { amount: 1000, label: '$1,000', description: 'Home libraries for 20 children' },
    },
    monthly: {
      small: { amount: 10, label: '$10', description: '1 child every 5 months' },
      medium: { amount: 20, label: '$20', description: '1 child every 2.5 months' },
      large: { amount: 40, label: '$40', description: '~1 child per month' },
      xlarge: { amount: 100, label: '$100', description: '2 children per month' },
      classroom: { amount: 200, label: '$200', description: '4 children per month' },
    }
  };

  const currentPackages = packages[donationType];
  
  // Calculate amount based on selection
  const getSelectedAmount = () => {
    if (selectedPackage === 'custom') {
      return parseFloat(customAmount) || 0;
    }
    return currentPackages[selectedPackage]?.amount || 0;
  };

  const selectedAmount = getSelectedAmount();
  
  const childrenPerYear = donationType === 'once'
    ? Math.floor(selectedAmount / 50)
    : Math.floor((selectedAmount * 12) / 50);

  const handlePackageClick = (packageId) => {
    setSelectedPackage(packageId);
    if (packageId !== 'custom') {
      setCustomAmount('');
    }
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
  };

  const handleDonate = async () => {
    // Basic validation
    if (!donorFirstName.trim() || !donorLastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    if (!donorEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(donorEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate custom amount if selected
    if (selectedPackage === 'custom') {
      const amount = parseFloat(customAmount);
      if (!customAmount || isNaN(amount)) {
        toast.error('Please enter a valid donation amount');
        return;
      }
      if (amount < 10) {
        toast.error('Minimum donation is $10');
        return;
      }
      if (amount > 10000) {
        toast.error('Maximum donation is $10,000');
        return;
      }
    }

    setLoading(true);

    try {
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;

      // Prepare request payload
      const payload = {
        package_id: selectedPackage,
        donation_type: donationType,
        origin_url: originUrl,
        donor_name: `${donorFirstName} ${donorLastName}`,
        donor_first_name: donorFirstName,
        donor_last_name: donorLastName,
        donor_email: donorEmail
      };

      // Add custom amount if applicable
      if (selectedPackage === 'custom') {
        payload.custom_amount = parseFloat(customAmount);
      }

      // Call backend to create checkout session
      const response = await axios.post(`${API}/donations/create-checkout`, payload);

      if (response.data.success && response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process donation. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5f7] to-[#f0f8fa]">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white py-16 bg-cover bg-top" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_bookshelf-builder/artifacts/9nh0qnj4_THLP%20-%20Donate.jpg')` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c5570]/85 to-[#14919f]/85"></div>
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            Give the Gift of Books
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Every $50 will provide 10 curated books to one child for an entire year
          </p>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl border-0 rounded-xl" style={{letterSpacing: '0'}}>
            <CardContent className="p-0">
              {/* Tabs */}
              <div className="grid grid-cols-2">
                <button
                  onClick={() => setDonationType('once')}
                  className={`py-6 text-xl font-bold uppercase tracking-wide transition-all ${
                    donationType === 'once'
                      ? 'bg-[#fad53a] text-[#2c5570]'
                      : 'bg-white text-[#2c5570]/60 hover:text-[#2c5570]'
                  }`}
                  data-testid="give-once-tab"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                >
                  Give Once
                </button>
                <button
                  onClick={() => setDonationType('monthly')}
                  className={`py-6 text-xl font-bold uppercase tracking-wide transition-all ${
                    donationType === 'monthly'
                      ? 'bg-[#fad53a] text-[#2c5570]'
                      : 'bg-white text-[#2c5570]/60 hover:text-[#2c5570]'
                  }`}
                  data-testid="monthly-tab"
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                >
                  Monthly
                </button>
              </div>

              {/* Amount Selection */}
              <div className="p-8 sm:p-12 bg-white">
                <h2 className="text-2xl font-bold text-[#2c5570] mb-8 text-center">
                  {donationType === 'once' ? 'Choose an amount to give' : 'Choose an amount to give per month'}
                </h2>

                {/* Amount Buttons Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  {Object.entries(currentPackages).map(([packageId, pkg]) => (
                    <button
                      key={packageId}
                      onClick={() => handlePackageClick(packageId)}
                      className={`p-6 rounded-xl border-2 transition-all ${
                        selectedPackage === packageId
                          ? 'border-[#14919f] bg-[#e8f5f7] shadow-lg'
                          : 'border-gray-200 hover:border-[#14919f]/50'
                      }`}
                      data-testid={`amount-${pkg.amount}`}
                    >
                      <div className="text-3xl font-black text-[#2c5570] mb-2" style={{ fontFamily: "'Rubik', sans-serif", letterSpacing: '-0.02em' }}>
                        {pkg.label}
                      </div>
                      <div className="text-xs text-[#4a4a4a]">
                        {pkg.description}
                      </div>
                    </button>
                  ))}
                  
                  {/* Custom Amount Button */}
                  <button
                    onClick={() => handlePackageClick('custom')}
                    className={`p-6 rounded-xl border-2 transition-all ${
                      selectedPackage === 'custom'
                        ? 'border-[#14919f] bg-[#e8f5f7] shadow-lg'
                        : 'border-gray-200 hover:border-[#14919f]/50'
                    }`}
                    data-testid="amount-custom"
                  >
                    <div className="text-2xl font-black text-[#2c5570] mb-2" style={{ fontFamily: "'Rubik', sans-serif" }}>
                      Other
                    </div>
                    <div className="text-xs text-[#4a4a4a]">
                      Custom amount
                    </div>
                  </button>
                </div>

                {/* Custom Amount Input */}
                {selectedPackage === 'custom' && (
                  <div className="mb-8">
                    <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                      Enter Custom Amount ($10 - $10,000)
                    </label>
                    <div className="relative">
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                      <Input
                        type="text"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        placeholder="100"
                        className="rounded-[50px] border-2 border-gray-300 focus:border-[#14919f] pl-8 pr-5 py-3 text-lg"
                        data-testid="custom-amount-input"
                      />
                    </div>
                  </div>
                )}

                {/* Donor Information */}
                <div className="mb-8 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={donorFirstName}
                        onChange={(e) => setDonorFirstName(e.target.value)}
                        placeholder="First Name"
                        className="rounded-[50px] border-2 border-gray-300 focus:border-[#14919f] px-5 py-3"
                        data-testid="donor-first-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={donorLastName}
                        onChange={(e) => setDonorLastName(e.target.value)}
                        placeholder="Last Name"
                        className="rounded-[50px] border-2 border-gray-300 focus:border-[#14919f] px-5 py-3"
                        data-testid="donor-last-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="rounded-[50px] border-2 border-gray-300 focus:border-[#14919f] px-5 py-3"
                      data-testid="donor-email-input"
                    />
                    <p className="text-xs text-[#4a4a4a] mt-2">
                      We'll send your donation receipt to this email address
                    </p>
                  </div>
                </div>

                {/* Impact Display */}
                {selectedAmount >= 10 && (
                  <div className="bg-gradient-to-br from-[#e8f5f7] to-[#f0f8fa] rounded-2xl p-8 mb-8 text-center">
                    {donationType === 'monthly' || selectedAmount >= 50 ? (
                      <>
                        <div className="text-3xl sm:text-5xl font-black text-[#14919f] mb-3 leading-snug" style={{ fontFamily: "'Rubik', sans-serif", letterSpacing: '-0.02em' }}>
                          {childrenPerYear} {childrenPerYear === 1 ? 'child' : 'children'}{donationType === 'monthly' ? ' annually' : ''}
                        </div>
                        <div className="text-sm text-[#4a4a4a]">
                          That's {childrenPerYear * 10} carefully selected books for their home {childrenPerYear === 1 ? 'library' : 'libraries'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-4xl font-black text-[#14919f] mb-3" style={{ fontFamily: "'Rubik', sans-serif", letterSpacing: '-0.01em' }}>
                          Helping a Child Build Their Home Library
                        </div>
                        <div className="text-lg text-[#2c5570] mb-2" style={{ fontFamily: "'Rubik', sans-serif" }}>
                          {Math.floor(selectedAmount / 5)} {Math.floor(selectedAmount / 5) === 1 ? 'Book' : 'Books'} Purchased
                        </div>
                        <div className="text-sm text-[#4a4a4a]">
                          At $5 per book, your ${selectedAmount.toFixed(2)} gift helps build home libraries
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Donate Button */}
                <Button
                  onClick={handleDonate}
                  disabled={loading || (selectedPackage === 'custom' && !customAmount)}
                  className="w-full bg-[#b63435] hover:bg-[#a02d2e] text-white font-bold text-xl py-6 rounded-[50px] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="donate-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : selectedPackage === 'custom' && customAmount ? (
                    `Donate $${parseFloat(customAmount).toFixed(2)} ${donationType === 'monthly' ? '/month' : ''}`
                  ) : selectedPackage === 'custom' ? (
                    'Enter Amount Above'
                  ) : (
                    `Donate ${currentPackages[selectedPackage].label} ${donationType === 'monthly' ? '/month' : ''}`
                  )}
                </Button>

                {/* Security Badges */}
                <div className="mt-8 flex justify-center items-center gap-6 text-sm text-[#4a4a4a]">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-[#14919f]" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-white border-[#14919f] text-[#14919f]">
                      501(c)(3) Nonprofit
                    </Badge>
                  </div>
                </div>

                <p className="text-xs text-center text-[#4a4a4a] mt-4">
                  Your donation is processed securely through Stripe. You'll be redirected to complete your payment.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-12 bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#2c5570] mb-8 text-center">Why Your Gift Matters</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-black text-[#14919f] mb-3" style={{ fontFamily: "'Rubik', sans-serif", letterSpacing: '-0.02em' }}>60%</div>
              <p className="text-[#4a4a4a]">of third graders in Delaware are not reading on grade level</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-[#14919f] mb-3" style={{ fontFamily: "'Rubik', sans-serif", letterSpacing: '-0.02em' }}>$50</div>
              <p className="text-[#4a4a4a]">will provide 10 books to one child's home library</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-[#14919f] mb-3" style={{ fontFamily: "'Rubik', sans-serif", letterSpacing: '-0.02em' }}>100%</div>
              <p className="text-[#4a4a4a]">of every donation directly funds the mission</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
